import { useState, useMemo, useCallback } from 'react'
import useSWR, { SWRConfiguration, mutate } from 'swr'
import { toast } from 'react-toastify'
import { planningApi } from '../services/api'
import { IContest, IContestApiResponse } from '../types'

interface UseContestsParams {
  search?: string
  order?: 'new' | 'old'
}

export const useContests = (params: UseContestsParams = {}) => {
  const [search, setSearch] = useState(params.search || '')
  const [order, setOrder] = useState<'new' | 'old'>(params.order || 'new')
  const [allContests, setAllContests] = useState<IContest[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(true)

  // Generate cache key based on search term and page
  const cacheKey = search.trim()
    ? `/study-plan/contests?search=${encodeURIComponent(
        search.trim()
      )}&page=${currentPage}&limit=12`
    : `/study-plan/contests?page=${currentPage}&limit=12`

  const swr = useSWR<IContestApiResponse>(cacheKey, fetcher, configuration)

  // Memoized data processing
  const data = useMemo(() => {
    if (!swr.data) return allContests

    const newContests = swr.data.data
    let processedContests = [...allContests, ...newContests]

    // Remove duplicates based on _id
    processedContests = processedContests.filter(
      (contest, index, self) => index === self.findIndex((c) => c._id === contest._id)
    )

    return processedContests
  }, [swr.data, allContests, order])

  // Update local state when new data arrives
  useMemo(() => {
    if (swr.data) {
      setAllContests((prev) => {
        const newContests = swr.data!.data
        const combined = [...prev, ...newContests]

        // Remove duplicates
        return combined.filter(
          (contest, index, self) => index === self.findIndex((c) => c._id === contest._id)
        )
      })

      setHasNextPage(swr.data.metadata.hasNextPage)
    }
  }, [swr.data])

  // Search function with server-side filtering
  const searchContests = useCallback(
    (searchTerm: string) => {
      setSearch(searchTerm)
      setCurrentPage(1)
      setAllContests([]) // Reset contests when searching
    },
    [setSearch]
  )

  // Load more contests for infinite scroll
  const loadMoreContests = useCallback(() => {
    if (hasNextPage && !swr.isLoading) {
      setCurrentPage((prev) => prev + 1)
    }
  }, [hasNextPage, swr.isLoading])

  // Reset contests (useful for clearing search)
  const resetContests = useCallback(() => {
    setSearch('')
    setCurrentPage(1)
    setAllContests([])
    setHasNextPage(true)
  }, [setSearch])

  // Refresh contests data
  const refreshContests = useCallback(() => {
    mutate(cacheKey)
  }, [cacheKey])

  return {
    contests: data,
    loading: swr.isLoading,
    error: swr.error,
    pagination: {
      page: currentPage,
      hasNextPage,
      hasPrevPage: currentPage > 1,
    },
    search,
    order,
    setOrder,
    searchContests,
    loadMoreContests,
    resetContests,
    refreshContests,
    // Legacy API compatibility
    fetchContests: refreshContests,
  }
}

// SWR fetcher function
const fetcher = (url: string) => {
  const urlObj = new URL(url, window.location.origin)
  const search = urlObj.searchParams.get('search')
  const page = parseInt(urlObj.searchParams.get('page') || '1')
  const limit = parseInt(urlObj.searchParams.get('limit') || '12')

  return planningApi.getContests(page, limit, search || undefined)
}

// SWR configuration
const configuration: SWRConfiguration = {
  fallbackData: undefined,
  shouldRetryOnError: true,
  onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
    if (retryCount > 3) {
      toast.error('Não foi possível carregar os concursos. Tente novamente mais tarde.')
    } else {
      setTimeout(() => revalidate({ retryCount }), 1000 * Math.pow(2, retryCount))
    }
  },
  dedupingInterval: 60e3, // 1 minute
  revalidateOnReconnect: true,
  revalidateOnFocus: false,
  refreshInterval: 10 * 60 * 1000, // 10 minutes cache invalidation
  errorRetryCount: 3,
  onError: (error) => {
    console.error('SWR Error fetching contests:', error)
    toast.error('Erro ao carregar concursos. Tente novamente.')
  },
}
